import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { ElementRef } from '@angular/core';
import { IqEventBusTransformationService } from '@dat/iq-event-bus';
import { sortBy } from 'lodash';
import { DateTime } from 'luxon';
import { GraphColors } from '../models';

/**
 * The rate types that are used to help coordinate showing series.
 * These will be used to help components on the outside to update the graph
 * without having to know too much about the inner workings of the graph.
 */
export type RateSeriesTypes =
    | 'brokerCarrierSpotForecast'
    | 'shipperCarrierContractForecast'
    | 'brokerCarrierSpotHistorical'
    | 'shipperCarrierContractHistorical';

/**
 * The legend settings for the legend component. This will help
 * the settings for the legend component to know what to show and what not to show.
 */
export type LegendSettings = {
    /**
     * The title of the legend.
     */
    title: string;
    /**
     * The color of the icon for the series.
     */
    color: string;
    /**
     * The id of this legend (used for targetting)
     */
    id: string;

    /**
     * Determines if this legend has a tooltip
     */
    tooltip: string;

    /**
     * Determines if the current legend is checked.
     *
     * If checked, it will show the various series
     */
    checked: boolean;

    /**
     * Determines if the legend is shown.
     */
    show: boolean;

    /**
     * Determines if the legend is a toggle allowing the
     * user to show and hide the series
     */
    toggle: boolean;

    /**
     * The series ids that are associated with this legend.
     */
    seriesIds: string[];
};

//#region Historical Legend

/**
 * The historical series ids that are available for the graph.
 * THis is to make it easier for the legend to toggle them off and on
 */
export type HistoricalSeriesIds =
    | 'brokerCarrierSpotHistoricalLineSeriesHybrid'
    | 'brokerCarrierSpotHistoricalCandleStickSeriesHybrid'
    | 'shipperCarrierContractHistoricalLineSeriesHybrid'
    | 'shipperCarrierContractHistoricalCandleStickSeriesHybrid'
    | 'brokerCarrierSpotCurrentCandleStickSeriesHybrid'
    | 'brokerCarrierSpotCurrentLineSeriesHybrid'
    | 'shipperCarrierContractCurrentCandleStickSeriesHybrid'
    | 'shipperCarrierContractCurrentLineSeriesHybrid';

/**
 * The ids for the historical series that are available for the graph.
 */
export const bcsHistoricalLineSeriesId: HistoricalSeriesIds =
    'brokerCarrierSpotHistoricalLineSeriesHybrid';
export const bcsHistoricalCandlestickSeriesId: HistoricalSeriesIds =
    'brokerCarrierSpotHistoricalCandleStickSeriesHybrid';
export const bcsCurrentCandlestickSeriesId: HistoricalSeriesIds =
    'brokerCarrierSpotCurrentCandleStickSeriesHybrid';
export const bcsCurrentLineSeriesId: HistoricalSeriesIds =
    'brokerCarrierSpotCurrentLineSeriesHybrid';
export const sccHistoricalLineSeriesId: HistoricalSeriesIds =
    'shipperCarrierContractHistoricalLineSeriesHybrid';
export const sccHistoricalCandlestickSeriesId: HistoricalSeriesIds =
    'shipperCarrierContractHistoricalCandleStickSeriesHybrid';
export const sccCurrentCandlestickSeriesId: HistoricalSeriesIds =
    'shipperCarrierContractCurrentCandleStickSeriesHybrid';
export const sccCurrentLineSeriesId: HistoricalSeriesIds =
    'shipperCarrierContractCurrentLineSeriesHybrid';

/**
 * The historical series ids that are available for the graph.
 *
 * This is to help coordinate the forecast vs histoqrical series.
 */
export const historicalIdList: HistoricalSeriesIds[] = [
    bcsHistoricalLineSeriesId,
    bcsHistoricalCandlestickSeriesId,
    sccHistoricalLineSeriesId,
    sccHistoricalCandlestickSeriesId,
];

/**
 * The forecast series ids that are available for the graph.
 * THis is to make it easier for the legend to toggle them off and on
 */
export type ForecastSeriesIds =
    | 'brokerCarrierSpotForecastLineSeriesHybrid'
    | 'brokerCarrierSpotForecastAreaSeriesHybrid'
    | 'shipperCarrierContractForecastLineSeriesHybrid'
    | 'shipperCarrierContractForecastAreaSeriesHybrid';

/**
 * The ids for the forecast series that are available for the graph.
 */
export const bcsForecastLineSeriesId: ForecastSeriesIds =
    'brokerCarrierSpotForecastLineSeriesHybrid';
export const bcsForecastAreaSeriesId: ForecastSeriesIds =
    'brokerCarrierSpotForecastAreaSeriesHybrid';
export const sccForecastLineSeriesId: ForecastSeriesIds =
    'shipperCarrierContractForecastLineSeriesHybrid';
export const sccForecastAreaSeriesId: ForecastSeriesIds =
    'shipperCarrierContractForecastAreaSeriesHybrid';

export const isHistoricalLineSeries = (id: string): boolean => {
    return historicalIdList.includes(id as HistoricalSeriesIds);
};

export const legendMap: Record<RateSeriesTypes, LegendSettings> = {
    brokerCarrierSpotHistorical: {
        title: 'Historical Spot',
        color: GraphColors.BrokerCarrierSpot,
        id: 'historicalBrokerCarrierSpot',
        tooltip: '',
        checked: false,
        toggle: false,
        seriesIds: [
            bcsHistoricalLineSeriesId,
            bcsHistoricalCandlestickSeriesId,
        ],
        show: false,
    },
    shipperCarrierContractHistorical: {
        title: 'Historical Contract',
        color: GraphColors.ShipperCarrierContract,
        id: 'historicalBrokerCarrierContract',
        tooltip: '',
        checked: false,
        seriesIds: [
            sccHistoricalLineSeriesId,
            sccHistoricalCandlestickSeriesId,
        ],
        show: false,
        toggle: false,
    },
    brokerCarrierSpotForecast: {
        title: 'Forecast Spot',
        color: GraphColors.BrokerCarrierSpot,
        id: 'forecastBrokerCarrierSpot',
        tooltip: '',
        checked: false,
        toggle: false,
        seriesIds: [bcsForecastLineSeriesId, bcsForecastAreaSeriesId],
        show: false,
    },
    shipperCarrierContractForecast: {
        title: 'Forecast Contract',
        color: GraphColors.ShipperCarrierContract,
        id: 'forecastBrokerCarrierContract',
        tooltip: '',
        checked: false,
        seriesIds: [sccForecastLineSeriesId, sccForecastAreaSeriesId],
        show: false,
        toggle: false,
    },
};

//#endregion

//#region Forecast Legend

//#endregion

export type RateSeriesData = {
    [key: string]: {
        date: number;
        high: number;
        key: string;
        low: number;
        average: number;
        timeEscalation: string;
    };
};

//#region Current Graph
export function setUpCurrentGraph(
    container: ElementRef,
    chartInfo: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
    },
): {
    chart: am5xy.XYChart;
    root: am5.Root;
    xAxis: am5xy.CategoryAxis<any>;
    yAxis: am5xy.ValueAxis<any>;
} {
    const root = am5.Root.new(container.nativeElement, {
        tooltipContainerBounds: {
            top: 300,
            bottom: 100,
            left: 50,
            right: 50,
        },
    });

    root.timezone = am5.Timezone.new('UTC');

    //#region Set Up Chart
    const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
            layout: root.verticalLayout,
        }),
    );
    //#endregion

    //#region Set Up X Axes
    let xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
            renderer: am5xy.AxisRendererX.new(root, {}),
            width: 80,
            paddingTop: 5,
            categoryField: 'category',
        }),
    );

    let xRenderer = xAxis.get('renderer');

    xRenderer.labels.template.setAll({
        rotation: 60,
        fontSize: 12,
        fill: am5.color('#636D79'),
        fontFamily: 'Sequel Sans',
        paddingTop: 15,
        location: 1,
    });

    xRenderer.grid.template.setAll({
        strokeWidth: 0,
        visible: false,
    });

    xAxis.set(
        'tooltip',
        am5.Tooltip.new(root, {
            themeTags: ['axis'],
            forceHidden: true,
        }),
    );
    //#endregion

    //#region Set Up Y Axes

    const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
            renderer: am5xy.AxisRendererY.new(root, {}),
            height: 260,
            autoZoom: false,
            paddingRight: 6,
            marginLeft: -8, // Default margin is taking around 24px.
        }),
    );

    let yRenderer = yAxis.get('renderer');

    yRenderer.grid.template.setAll({
        strokeDasharray: [4],
    });

    yRenderer.labels.template.setAll({
        fontSize: 10,
        fontFamily: 'Sequel Sans',
        fill: am5.color('#636D79'),
    });
    //#endregion

    return {
        chart,
        root,
        xAxis,
        yAxis,
    };
}
//#endregion

export function setUpGraph(container: ElementRef): {
    chart: am5xy.XYChart;
    root: am5.Root;
    xAxis: am5xy.CategoryAxis<any>;
    yAxis: am5xy.ValueAxis<any>;
} {
    const root = am5.Root.new(container.nativeElement, {
        tooltipContainerBounds: {
            top: 300,
            bottom: 100,
            left: 50,
            right: 50,
        },
    });
    root.timezone = am5.Timezone.new('UTC');
    const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
            panY: false,
            panX: false,
            layout: root.verticalLayout,
        }),
    );

    // Craete Y-axis
    var yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
            renderer: am5xy.AxisRendererY.new(root, {}),
            height: 260,
            autoZoom: false,
        }),
    );
    let yRenderer = yAxis.get('renderer');

    yRenderer.grid.template.setAll({
        strokeDasharray: [4],
    });

    yRenderer.labels.template.setAll({
        fontSize: 12,
        fontFamily: 'Sequel Sans',
        fill: am5.color('#636D79'),
    });

    // Create X-Axis
    let xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
            renderer: am5xy.AxisRendererX.new(root, {
                minGridDistance: 0,
                tooltip: am5.Tooltip.new(root, {
                    themeTags: ['axis'],
                    forceHidden: true,
                }),
            }),
            paddingTop: 6,
            categoryField: 'category',
            tooltip: am5.Tooltip.new(root, {
                themeTags: ['axis'],
                forceHidden: true,
            }),
        }),
    );
    xAxis
        .get('renderer')
        .labels.template.adapters.add('text', function (text, target) {
            if (target.dataItem && target.dataItem.dataContext) {
                // Cuts the year off the category name so that only Month is displayed
                return (target.dataItem.dataContext as any)?.category?.split(
                    ' ',
                )[0];
            }
            return text;
        });
    let xRenderer = xAxis.get('renderer');
    xRenderer.labels.template.setAll({
        fill: am5.color('#636D79'),
        fontFamily: 'Sequel Sans',
        fontSize: 12,
        rotation: 60,
        dx: 10,
        // maxPosition: 0.99,
    });
    xRenderer.grid.template.setAll({
        strokeWidth: 0,
        visible: false,
    });

    return {
        chart,
        root,
        xAxis,
        yAxis,
    };
}

type RateNode = {
    average: number;
    low: number;
    high: number;
    date: string;
};

type HybridGraphData = {
    category: string;
    brokerCarrierSpotHistorical: RateNode;
    brokerCarrierSpotForecast: RateNode;
    shipperCarrierContractHistorical: RateNode;
    shipperCarrierContractForecast: RateNode;
};

function formatHybridData(dataMap: Map<string, any>): HybridGraphData[] {
    return Array.from(dataMap).map(([key, value]) => {
        return { category: key, ...value };
    });
}

export function setCurrentGraphData(
    chartInfo: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
    },
    hybridData: Map<string, any>,
    currentData: Map<string, any>,
    rateType: 'BrokerCarrierSpot' | 'ShipperCarrierContract',
    perLoad: boolean,
    transformation: IqEventBusTransformationService,
) {
    const { chart, root, xAxis, yAxis } = chartInfo;
    chart.series.clear();
    const formattedCurrentData = formatHybridData(currentData);
    chartInfo.xAxis.data.setAll(formattedCurrentData);
    updateYAxis(chartInfo, hybridData, perLoad);

    yAxis.labelsContainer.set('visible', false);
    const [lineSeries, candlestickSeries] =
        rateType === 'BrokerCarrierSpot'
            ? createBrokerCarrierSpotCurrentSeries(
                  root,
                  chart,
                  xAxis,
                  yAxis,
                  formattedCurrentData,
              )
            : createShipperCarrierContractCurrentSeries(
                  root,
                  chart,
                  xAxis,
                  yAxis,
                  formattedCurrentData,
              );
    lineSeries.dataItems.forEach((dataItem: { dataContext: any }) => {
        const data = dataItem.dataContext;
        const template = transformation.render(
            rateType === 'BrokerCarrierSpot'
                ? createSpotTooltip()
                : createContractTooltip(),
            {
                title: `Historical ${
                    rateType === 'BrokerCarrierSpot' ? 'Spot' : 'Contract'
                }`,
                date: data?.category,
                average: data?.average,
                low: data?.low,
                high: data?.high,
                withCents: !perLoad,
            },
        );
        dataItem.dataContext = {
            ...dataItem.dataContext,
            template,
        };
    });

    const tooltip = (lineSeries as am5xy.XYSeries).set(
        'tooltip',
        am5.Tooltip.new(chartInfo.root, {
            animationDuration: 0,
            getFillFromSprite: false,
            getStrokeFromSprite: false,
        }),
    );

    chartInfo.chart.plotContainer.set('tooltipPosition', 'pointer');
    chartInfo.chart.plotContainer.set('tooltipHTML', '<div></div>');
    chartInfo.chart.plotContainer.set('tooltip', tooltip);

    const borderColor =
        rateType === 'BrokerCarrierSpot'
            ? GraphColors.BrokerCarrierSpot
            : GraphColors.ShipperCarrierContract;

    tooltip.get('background').setAll({
        strokeWidth: 1,
        fill: am5.color('#fff'),
        stroke: am5.color(borderColor),
        strokeOpacity: 1,
        fillOpacity: 1,
    });

    tooltip.adapters.add('pointTo', (pointTo) => {
        return {
            ...pointTo,
            y: 0,
        };
    });

    tooltip.label.adapters.add('dy', (dy) => {
        tooltip.adapters.add('bounds', function () {
            return {
                left: -80,
                right: 9999,
                top: 0,
                bottom: 20,
            };
        });
        return -tooltip._fy;
    });

    tooltip.label.adapters.add('html', function (text, _target) {
        return `{template}`;
    });
    const cursor: am5xy.XYCursor = am5xy.XYCursor.new(root, {
        behavior: 'none',
        snapToSeries: [lineSeries],
        snapToSeriesBy: 'x',
        xAxis,
    });
    cursor.lineY.setAll({
        visible: false,
    });
    chart.set('cursor', cursor);
    cursor.lineX.setAll({
        strokeDasharray: null,
        strokeWidth: 1,
        stroke: am5.color('#636d79'),
        dy: 0,
    });
   
}

export function setXAxisData(
    chartInfo: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
    },
    data: Map<string, any>,
    selectedRates: RateSeriesTypes[],
    rateType: 'BrokerCarrierSpot' | 'ShipperCarrierContract',
    perLoad: boolean,
    transformation: IqEventBusTransformationService,
) {
    const { chart, root, xAxis, yAxis } = chartInfo;
    chart.series.clear();
    const formattedData = formatHybridData(data);
    chartInfo.xAxis.data.setAll(formattedData);
    const historicalSeries = createHistoricalSeries(chartInfo, formattedData);
    const forecastSeries = createForecastSeries(chartInfo, formattedData);
    updateYAxis(chartInfo, data, perLoad);
    toggleSeriesVisibility(
        rateType,
        selectedRates,
        historicalSeries,
        forecastSeries,
    );
    createTooltips(chartInfo, rateType, perLoad, formattedData, transformation);
}

function updateYAxis(chartInfo, data: Map<string, any>, perLoad) {
    const { chart, root, xAxis, yAxis } = chartInfo;
    const formattedData = formatHybridData(data);
    const vals = [];
    data.forEach((category) => {
        Object.values(category)
            .filter((value) => typeof value === 'object')
            .forEach((value: any) => {
                vals.push({ high: value.high, low: value.low });
            });
    });

    const { min, max } = getMinMaxYValues(vals);
    yAxis.setAll({
        min: min,
        max: max,
        numberFormat: perLoad ? '$#' : '$#.00',
    });
}

export function toggleSeriesVisibility(
    rateType: string,
    selectedRates: RateSeriesTypes[],
    historicalSeries: {
        lineSeriesBrokerCarrierSpot: am5xy.LineSeries;
        candlesStickBrokerCarrierSpot: am5xy.CandlestickSeries;
        lineSeriesShipperCarrierContract: am5xy.LineSeries;
        candlesStickShipperCarrierContract: am5xy.CandlestickSeries;
    },
    forecastSeries: {
        lineSeriesBrokerCarrierSpot: am5xy.LineSeries;
        candlesStickBrokerCarrierSpot: am5xy.LineSeries;
        lineSeriesShipperCarrierContract: am5xy.LineSeries;
        candlesStickShipperCarrierContract: am5xy.LineSeries;
    },
) {
    if (rateType === 'ShipperCarrierContract') {
        if (!selectedRates.includes('brokerCarrierSpotHistorical')) {
            historicalSeries.lineSeriesBrokerCarrierSpot.hide();
            historicalSeries.candlesStickBrokerCarrierSpot.hide();
        } else {
            historicalSeries.lineSeriesBrokerCarrierSpot.show();
            historicalSeries.candlesStickBrokerCarrierSpot.show();
        }

        if (!selectedRates.includes('brokerCarrierSpotForecast')) {
            forecastSeries.lineSeriesBrokerCarrierSpot.hide();
            forecastSeries.candlesStickBrokerCarrierSpot.hide();
        } else {
            forecastSeries.lineSeriesBrokerCarrierSpot.show();
            forecastSeries.candlesStickBrokerCarrierSpot.show();
        }
    }
    if (rateType === 'BrokerCarrierSpot') {
        if (!selectedRates.includes('shipperCarrierContractHistorical')) {
            historicalSeries.lineSeriesShipperCarrierContract.hide();
            historicalSeries.candlesStickShipperCarrierContract.hide();
        } else {
            historicalSeries.lineSeriesShipperCarrierContract.show();
            historicalSeries.candlesStickShipperCarrierContract.show();
        }

        if (!selectedRates.includes('shipperCarrierContractForecast')) {
            forecastSeries.lineSeriesShipperCarrierContract.hide();
            forecastSeries.candlesStickShipperCarrierContract.hide();
        } else {
            forecastSeries.lineSeriesShipperCarrierContract.show();
            forecastSeries.candlesStickShipperCarrierContract.show();
        }
    }
}

export function createTooltips(
    {
        chart,
        root,
        xAxis,
        yAxis,
    }: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>;
    },
    rateType,
    perLoad: boolean,
    formattedData: HybridGraphData[],
    transformation: IqEventBusTransformationService,
) {
    const historicalId =
        rateType === 'BrokerCarrierSpot'
            ? bcsHistoricalLineSeriesId
            : sccHistoricalLineSeriesId;
    const forecastId =
        rateType === 'BrokerCarrierSpot'
            ? bcsForecastLineSeriesId
            : sccForecastLineSeriesId;

    const tooltipSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
            name: 'tooltip-series',
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: 'value',
            categoryXField: 'category',
        }),
    );
    tooltipSeries.strokes.template.setAll({
        opacity: 0,
    });
    const visibleSeries: (HistoricalSeriesIds | ForecastSeriesIds)[] = [];
    chart.series.each((series) => {
        if (series.get('visible')) {
            visibleSeries.push(series.get('id') as HistoricalSeriesIds);
        }
    });
    tooltipSeries.data.setAll(
        formattedData.map((data) => {
            const spotHistoricalData =
                data?.brokerCarrierSpotHistorical || undefined;
            const spotForecastData =
                data?.brokerCarrierSpotForecast || undefined;
            const contractHistoricalData =
                data?.shipperCarrierContractHistorical || undefined;
            const contractForecastData =
                data?.shipperCarrierContractForecast || undefined;
            let tooltips = [];

            if (
                spotHistoricalData &&
                visibleSeries.includes(bcsHistoricalLineSeriesId)
            ) {
                tooltips.push(
                    transformation.render(createSpotTooltip(), {
                        title: 'Historical Spot',
                        date: DateTime.fromISO(spotHistoricalData.date, {
                            zone: 'UTC',
                        }).toFormat('MMMM yyyy'),
                        average: spotHistoricalData?.average,
                        low: spotHistoricalData?.low,
                        high: spotHistoricalData?.high,
                        withCents: !perLoad,
                    }),
                );
            }
            if (
                spotForecastData &&
                visibleSeries.includes(bcsForecastLineSeriesId)
            ) {
                tooltips.push(
                    transformation.render(createSpotTooltip(), {
                        title: 'Forecast Spot',
                        date: DateTime.fromISO(spotForecastData.date, {
                            zone: 'UTC',
                        }).toFormat('MMMM yyyy'),
                        average: spotForecastData?.average,
                        low: spotForecastData?.low,
                        high: spotForecastData?.high,
                        withCents: !perLoad,
                    }),
                );
            }
            if (
                contractHistoricalData &&
                visibleSeries.includes(sccHistoricalLineSeriesId)
            ) {
                tooltips.push(
                    transformation.render(createContractTooltip(), {
                        title: 'Historical Contract',
                        date: DateTime.fromISO(contractHistoricalData.date, {
                            zone: 'UTC',
                        }).toFormat('MMMM yyyy'),
                        average: contractHistoricalData?.average,
                        low: contractHistoricalData?.low,
                        high: contractHistoricalData?.high,
                        withCents: !perLoad,
                    }),
                );
            }
            if (
                contractForecastData &&
                visibleSeries.includes(sccForecastLineSeriesId)
            ) {
                tooltips.push(
                    transformation.render(createContractTooltip(), {
                        title: 'Forecast Contract',
                        date: DateTime.fromISO(contractForecastData.date, {
                            zone: 'UTC',
                        }).toFormat('MMMM yyyy'),
                        average: contractForecastData?.average,
                        low: contractForecastData?.low,
                        high: contractForecastData?.high,
                        withCents: !perLoad,
                    }),
                );
            }
            return {
                category: data.category,
                value:
                    data?.brokerCarrierSpotHistorical?.average ??
                    data.brokerCarrierSpotForecast?.average,
                template: `<div class="mb-2 flex flex-row items-center justify-center gap-4 flex-wrap max-w-[280px]">${tooltips.join(
                    '',
                )}</div>`,
            };
        }),
    );

    const tooltip = tooltipSeries.set(
        'tooltip',
        am5.Tooltip.new(root, {
            animationDuration: 0,
            getFillFromSprite: false,
            getStrokeFromSprite: false,
        }),
    );
    chart.plotContainer.set('tooltipPosition', 'pointer');
    chart.plotContainer.set('tooltipHTML', '<div></div>');
    chart.plotContainer.set('tooltip', tooltip);

    const isSpotShowingContract =
        rateType === 'BrokerCarrierSpot' &&
        (visibleSeries.includes(sccHistoricalLineSeriesId) ||
            visibleSeries.includes(sccForecastLineSeriesId));

    const isContractShowingSpot =
        rateType === 'ShipperCarrierContract' &&
        (visibleSeries.includes(bcsForecastLineSeriesId) ||
            visibleSeries.includes(bcsHistoricalLineSeriesId));
 
    const borderColor =
        isSpotShowingContract || isContractShowingSpot
            ? '#000'
            : rateType === 'BrokerCarrierSpot'
            ? GraphColors.BrokerCarrierSpot
            : GraphColors.ShipperCarrierContract;

    tooltip.get('background').setAll({
        strokeWidth: 1,
        fill: am5.color('#fff'),
        stroke: am5.color(borderColor),
        strokeOpacity: 1,
        fillOpacity: 1,
    });
    tooltip.adapters.add('pointTo', (pointTo) => {
        return {
            ...pointTo,
            y: 0,
        };
    });
    tooltip.label.adapters.add('dy', (dy) => {
        tooltip.adapters.add('bounds', function () {
            return {
                left: 70,
                right: 9999,
                top: 0,
                bottom: 20,
            };
        });
        return -tooltip._fy;
    });
    tooltip.label.adapters.add('html', function (text, _target) {
        return `{template}`;
    });
    const cursor: am5xy.XYCursor = am5xy.XYCursor.new(root, {
        behavior: 'none',
        snapToSeries: [tooltipSeries],
        snapToSeriesBy: 'x',
        xAxis,
    });
    cursor.lineY.setAll({
        visible: false,
    });
    chart.set('cursor', cursor);
    cursor.lineX.setAll({
        strokeDasharray: null,
        strokeWidth: 1,
        stroke: am5.color('#636d79'),
        dy: 0,
    });
}

export function createHistoricalSeries(
    {
        chart,
        root,
        xAxis,
        yAxis,
    }: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>;
    },
    data: HybridGraphData[],
): {
    lineSeriesBrokerCarrierSpot: am5xy.LineSeries;
    candlesStickBrokerCarrierSpot: am5xy.CandlestickSeries;
    lineSeriesShipperCarrierContract: am5xy.LineSeries;
    candlesStickShipperCarrierContract: am5xy.CandlestickSeries;
} {
    const [lineSeriesBrokerCarrierSpot, candlesStickBrokerCarrierSpot] =
        createBrokerCarrierSpotHistoricalSeries(
            data,
            { chart, root, xAxis, yAxis },
            { perLoad: false },
        );
    const [
        lineSeriesShipperCarrierContract,
        candlesStickShipperCarrierContract,
    ] = createShipperCarrierContractHistoricalSeries(
        data,
        { chart, root, xAxis, yAxis },
        { perLoad: false },
    );
    return {
        lineSeriesBrokerCarrierSpot,
        candlesStickBrokerCarrierSpot,
        lineSeriesShipperCarrierContract,
        candlesStickShipperCarrierContract,
    };
}

export function createForecastSeries(
    {
        chart,
        root,
        xAxis,
        yAxis,
    }: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>;
    },
    data: any[],
): {
    lineSeriesBrokerCarrierSpot: am5xy.LineSeries;
    candlesStickBrokerCarrierSpot: am5xy.LineSeries;
    lineSeriesShipperCarrierContract: am5xy.LineSeries;
    candlesStickShipperCarrierContract: am5xy.LineSeries;
} {
    const [lineSeriesBrokerCarrierSpot, candlesStickBrokerCarrierSpot] =
        createBrokerCarrierSpotForecastSeries(
            data,
            { chart, root, xAxis, yAxis },
            { perLoad: false },
        );

    const [
        lineSeriesShipperCarrierContract,
        candlesStickShipperCarrierContract,
    ] = createShipperCarrierContractForecastSeries(
        data,
        { chart, root, xAxis, yAxis },
        { perLoad: false },
    );

    return {
        lineSeriesBrokerCarrierSpot,
        candlesStickBrokerCarrierSpot,
        lineSeriesShipperCarrierContract,
        candlesStickShipperCarrierContract,
    };
}

//#region Forecast Graph Series
export function updateForecastGraph(
    {
        chart,
        root,
        xAxis,
        yAxis,
    }: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>;
    },
    {
        brokerCarrierSpotForecast,
        shipperCarrierContractForecast,
        currentRate,
    }: {
        brokerCarrierSpotForecast: RateSeriesData;
        shipperCarrierContractForecast: RateSeriesData;
        currentRate: 'BrokerCarrierSpot' | 'ShipperCarrierContract';
    },
    {
        perLoad,
    }: {
        perLoad: boolean;
    },
    { timeframe }: { timeframe: 'DAILY' | 'MONTHLY' },
) {
    //#region Series Set UP

    const brokerCarrierSpotForecastSeries: any[] =
        createBrokerCarrierSpotForecastSeries(
            brokerCarrierSpotForecast,
            {
                chart,
                root,
                xAxis,
                yAxis,
            },
            {
                perLoad,
            },
        );

    const shipperCarrierContractForecastSeries: any[] =
        createShipperCarrierContractForecastSeries(
            shipperCarrierContractForecast,
            {
                chart,
                root,
                xAxis,
                yAxis,
            },
            {
                perLoad,
            },
        );
    const cursor: am5xy.XYCursor = am5xy.XYCursor.new(root, {
        behavior: 'none',
        snapToSeries: [
            currentRate === 'BrokerCarrierSpot'
                ? brokerCarrierSpotForecastSeries[0]
                : shipperCarrierContractForecastSeries[0],
        ],
        snapToSeriesBy: 'x',
        xAxis,
    });
    cursor.lineY.setAll({
        visible: false,
    });
    chart.set('cursor', cursor);
    cursor.lineX.setAll({
        strokeDasharray: null,
        strokeWidth: 1,
        stroke: am5.color('#636d79'),
        dy: 0,
    });

    //#region Y Axis Update

    /**
     * Clear the axis ranges and create new ones based on the new data.
     */
    yAxis.axisRanges.clear();

    /**
     * Get the min and max values from data to show on the graph
     */
    const { min, max } = getMinMaxYValues([
        ...Object.values(brokerCarrierSpotForecast),
        ...Object.values(shipperCarrierContractForecast),
    ]);

    yAxis.setAll({
        min: min,
        max: max,
        numberFormat: perLoad ? '$#' : '$#.00',
    });

    // yAxis.set('step', rangeStep);
    //#endregion

    chart.appear(1);

    return {
        chart,
        root,
        xAxis,
        yAxis,
    };
}

//#endregion

//#region Shipper Carrier Contract Forecast Series

function createShipperCarrierContractForecastSeries(
    shipperCarrierContractForecast,
    {
        chart,
        root,
        xAxis,
        yAxis,
    }: {
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
        root: am5.Root;
        chart: am5xy.XYChart;
    },
    {
        perLoad,
    }: {
        perLoad: boolean;
    },
): [am5xy.LineSeries, am5xy.LineSeries] {
    const area: am5xy.LineSeries = createShipperCarrierContractForecastArea(
        root,
        xAxis,
        yAxis,
        shipperCarrierContractForecast,
    );

    const lineSeries: am5xy.LineSeries =
        createShipperCarrierContractForecastLineSeries(
            root,
            xAxis,
            yAxis,
            shipperCarrierContractForecast,
        );

    // chart.series.push(candleStick);
    chart.series.push(lineSeries);
    chart.series.push(area);

    return [lineSeries, area];
}

function createShipperCarrierContractForecastLineSeries(
    root: am5.Root,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    ShipperCarrierContractForecast: any,
) {
    const lineSeries: am5xy.LineSeries = am5xy.LineSeries.new(root, {
        id: sccForecastLineSeriesId,
        name: 'forecastAverage',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'average',
        categoryXField: 'category',
        stroke: am5.color(GraphColors.ShipperCarrierContract),
    } as am5xy.ILineSeriesSettings);

    lineSeries.strokes.template.set('strokeDasharray', [3, 4]);

    lineSeries.data.setAll(
        ShipperCarrierContractForecast.map((data) => {
            return {
                category: data?.category,
                average: data['shipperCarrierContractForecast']?.average,
                high: data['shipperCarrierContractForecast']?.high,
                low: data['shipperCarrierContractForecast']?.low,
                date: data['shipperCarrierContractForecast']?.date,
            };
        }),
    );
    return lineSeries;
}

function createShipperCarrierContractForecastArea(
    root: am5.Root,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    shipperCarrierContractForecast: any,
): am5xy.LineSeries {
    const area: am5xy.LineSeries = am5xy.LineSeries.new(root, {
        id: sccForecastAreaSeriesId,
        name: 'forecastHighLow',
        xAxis: xAxis,
        yAxis: yAxis,
        openValueYField: 'high',
        valueYField: 'low',
        categoryXField: 'category',
        stroke: am5.color(GraphColors.ShipperCarrierContract),

        fill: am5.color(GraphColors.ShipperCarrierContract),
    } as am5xy.ILineSeriesSettings);

    area.fills.template.setAll({
        fillOpacity: 0.1,
        visible: true,
    });
    area.strokes.template.setAll({
        opacity: 0.1,
    });

    area.data.setAll(
        shipperCarrierContractForecast.map((data) => {
            return {
                category: data?.category,
                average: data['shipperCarrierContractForecast']?.average,
                high: data['shipperCarrierContractForecast']?.high,
                low: data['shipperCarrierContractForecast']?.low,
                date: data['shipperCarrierContractForecast']?.date,
            };
        }),
    );
    return area;
}
//#endregion

//#region Broker Carrier Spot Forecast Series

function createBrokerCarrierSpotForecastSeries(
    BrokerCarrierSpotForecast,
    {
        chart,
        root,
        xAxis,
        yAxis,
    }: {
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
        root: am5.Root;
        chart: am5xy.XYChart;
    },
    {
        perLoad,
    }: {
        perLoad: boolean;
    },
): [am5xy.LineSeries, am5xy.LineSeries] {
    const area: am5xy.LineSeries = createBrokerCarrierSpotForecastArea(
        root,
        xAxis,
        yAxis,
        BrokerCarrierSpotForecast,
    );

    const lineSeries: am5xy.LineSeries =
        createBrokerCarrierSpotForecastLineSeries(
            root,
            xAxis,
            yAxis,
            BrokerCarrierSpotForecast,
        );
    chart.series.push(lineSeries);
    chart.series.push(area);

    return [lineSeries, area];
}

function createBrokerCarrierSpotForecastLineSeries(
    root: am5.Root,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    BrokerCarrierSpotForecast: any,
) {
    const lineSeries: am5xy.LineSeries = am5xy.LineSeries.new(root, {
        id: bcsForecastLineSeriesId,
        name: 'forecastAverage',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'average',
        categoryXField: 'category',
        stroke: am5.color(GraphColors.BrokerCarrierSpot),
    } as am5xy.ILineSeriesSettings);

    lineSeries.strokes.template.set('strokeDasharray', [3, 4]);

    lineSeries.data.setAll(
        BrokerCarrierSpotForecast.map((data) => {
            return {
                category: data?.category,
                average: data['brokerCarrierSpotForecast']?.average,
                high: data['brokerCarrierSpotForecast']?.high,
                low: data['brokerCarrierSpotForecast']?.low,
                date: data['brokerCarrierSpotForecast']?.date,
            };
        }),
    );
    return lineSeries;
}

function createBrokerCarrierSpotForecastArea(
    root: am5.Root,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    BrokerCarrierSpotForecast: any,
): am5xy.LineSeries {
    const area: am5xy.LineSeries = am5xy.LineSeries.new(root, {
        id: bcsForecastAreaSeriesId,
        name: 'forecastHighLow',
        xAxis: xAxis,
        yAxis: yAxis,
        openValueYField: 'high',
        valueYField: 'low',
        categoryXField: 'category',
        stroke: am5.color(GraphColors.BrokerCarrierSpot),

        fill: am5.color(GraphColors.BrokerCarrierSpot),
    } as am5xy.ILineSeriesSettings);

    area.fills.template.setAll({
        fillOpacity: 0.1,
        visible: true,
    });
    area.strokes.template.setAll({
        opacity: 0.1,
    });

    area.data.setAll(
        BrokerCarrierSpotForecast.map((data) => {
            return {
                category: data?.category,
                average: data['brokerCarrierSpotForecast']?.average,
                high: data['brokerCarrierSpotForecast']?.high,
                low: data['brokerCarrierSpotForecast']?.low,
                date: data['brokerCarrierSpotForecast']?.date,
            };
        }),
    );
    return area;
}

export type HistoricalGraphInfo = {
    perLoad: boolean;
    currentRate: 'BrokerCarrierSpot' | 'ShipperCarrierContract';
    currentGraphChartInfo: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>;
    };
    brokerCarrierSpotHistorical: RateSeriesData;
    shipperCarrierContractHistorical: RateSeriesData;
    brokerCarrierSpotCurrent: RateSeriesData;
    shipperCarrierContractCurrent: RateSeriesData;
};

export type AllGraphInfo = {
    perLoad: boolean;
    currentRate: 'BrokerCarrierSpot' | 'ShipperCarrierContract';
    currentGraphChartInfo: {
        chart: am5xy.XYChart;
        root: am5.Root;
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>;
    };
    brokerCarrierSpotHistorical: RateSeriesData;
    shipperCarrierContractHistorical: RateSeriesData;
    brokerCarrierSpotCurrent: RateSeriesData;
    shipperCarrierContractCurrent: RateSeriesData;
    brokerCarrierSpotForecast: RateSeriesData;
    shipperCarrierContractForecast: RateSeriesData;
};

//#endregion

//#region Historical Graph Series

//#endregion

//#region Tooltip Templates
function createSpotTooltip() {
    return `<div class="bg-white flex flex-col justify-center w-[120px]">
        <label class="text-center text-dat-flame-400 text-nowrap pb-1 font-caption-strong"><%= title %></label>
        <div class="text-center text-dat-grey-900 pb-1 font-caption">
            <%= date %>
        </div>
        <div class="text-center font-caption-strong text-dat-grey-900">
            <%=  currency(average, withCents) %>
        </div>
        <div class="text-center font-caption text-dat-grey-900">
            <%=  currency(low, withCents) %>-<%=  currency(high, withCents) %>
        </div>
    </div>

`;
}
function createContractTooltip() {
    return `<div class="bg-white flex flex-col justify-center w-[120px]">
        <label class="text-center text-dat-blue-400 text-nowrap pb-1 font-caption-strong"><%= title %></label>
        <div class="text-center font-caption text-dat-grey-900 pb-1">
            <%= date %>
        </div>
        <div class="text-center font-caption-strong text-dat-grey-900">
             <%=  currency(average, withCents) %>
        </div>
        <div class="text-center font-caption text-dat-grey-900">
            <%=  currency(low, withCents) %>-<%=  currency(high, withCents) %>
        </div>
    </div>

`;
}

//#endregion

//#region Broker Carrier Spot Historical Series
function createBrokerCarrierSpotHistoricalSeries(
    brokerCarrierSpotHistorical,
    {
        chart,
        root,
        xAxis,
        yAxis,
    }: {
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
        root: am5.Root;
        chart: am5xy.XYChart;
    },
    {
        perLoad,
    }: {
        perLoad: boolean;
    },
): [am5xy.LineSeries, am5xy.CandlestickSeries] {
    const candleStick: am5xy.CandlestickSeries =
        createBrokerCarrierSpotHistoricalCandleStick(
            root,
            xAxis,
            yAxis,
            brokerCarrierSpotHistorical,
        );

    const lineSeries: am5xy.LineSeries =
        createBrokerCarrierSpotHistoricalLineSeries(
            root,
            xAxis,
            yAxis,
            brokerCarrierSpotHistorical,
        );

    chart.series.push(candleStick);
    chart.series.push(lineSeries);

    return [lineSeries, candleStick];
}

function createBrokerCarrierSpotHistoricalLineSeries(
    root: am5.Root,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    brokerCarrierSpotHistorical: HybridGraphData[],
) {
    const lineSeries: am5xy.LineSeries = am5xy.LineSeries.new(root, {
        id: bcsHistoricalLineSeriesId,
        name: 'HistoricalAverage',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'average',
        categoryXField: 'category',
        stroke: am5.color(GraphColors.BrokerCarrierSpot),
    } as am5xy.ILineSeriesSettings);

    addSquare(lineSeries, root, GraphColors.BrokerCarrierSpot);
    lineSeries.data.setAll(
        brokerCarrierSpotHistorical.map((data) => {
            return {
                category: data?.category,
                average: data['brokerCarrierSpotHistorical']?.average,
                high: data['brokerCarrierSpotHistorical']?.high,
                low: data['brokerCarrierSpotHistorical']?.low,
                date: data['brokerCarrierSpotHistorical']?.date,
            };
        }),
    );

    return lineSeries;
}

function createBrokerCarrierSpotHistoricalCandleStick(
    root: am5.Root,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    brokerCarrierSpotHistorical: HybridGraphData[],
): am5xy.CandlestickSeries {
    const candleStick: am5xy.CandlestickSeries = am5xy.CandlestickSeries.new(
        root,
        {
            id: bcsHistoricalCandlestickSeriesId,
            name: 'HistoricalHighLow',
            xAxis: xAxis,
            yAxis: yAxis,
            highValueYField: 'high',
            openValueYField: 'high',
            lowValueYField: 'low',
            valueYField: 'low',
            categoryXField: 'category',
            clustered: false,
        } as am5xy.ICandlestickSeriesSettings,
    );

    setCandlestickColor(candleStick, GraphColors.BrokerCarrierSpot);
    addBottomLine(candleStick, root, GraphColors.BrokerCarrierSpot);
    addTopLine(candleStick, root, GraphColors.BrokerCarrierSpot);
    setWidth(candleStick);

    candleStick.data.setAll(
        brokerCarrierSpotHistorical.map((data) => {
            return {
                category: data?.category,
                average: data['brokerCarrierSpotHistorical']?.average,
                high: data['brokerCarrierSpotHistorical']?.high,
                low: data['brokerCarrierSpotHistorical']?.low,
                date: data['brokerCarrierSpotHistorical']?.date,
            };
        }),
    );

    return candleStick;
}
//#endregion

//#region BCS Current Series
function createBrokerCarrierSpotCurrentSeries(
    root: am5.Root,
    chart: am5xy.XYChart,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    brokerCarrierSpotCurrent: HybridGraphData[],
): [am5xy.LineSeries, am5xy.CandlestickSeries] {
    const candleStick: am5xy.CandlestickSeries =
        createBrokerCarrierSpotCurrentCandleStick(
            root,
            xAxis,
            yAxis,
            brokerCarrierSpotCurrent,
        );
    const lineSeries: am5xy.LineSeries = createBrokerCarrierSpotCurrentLine(
        root,
        xAxis,
        yAxis,
        brokerCarrierSpotCurrent,
    );

    chart.series.push(candleStick);
    chart.series.push(lineSeries);

    return [lineSeries, candleStick];
}

function createBrokerCarrierSpotCurrentCandleStick(
    root: am5.Root,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    brokerCarrierSpotCurrent: HybridGraphData[],
): am5xy.CandlestickSeries {
    const candleStick: am5xy.CandlestickSeries = am5xy.CandlestickSeries.new(
        root,
        {
            id: bcsCurrentCandlestickSeriesId,
            name: 'CurrentHighLow',
            xAxis: xAxis,
            yAxis: yAxis,
            highValueYField: 'high',
            openValueYField: 'high',
            lowValueYField: 'low',
            valueYField: 'low',
            categoryXField: 'category',
            clustered: false,
        } as am5xy.ICandlestickSeriesSettings,
    );

    setCandlestickColor(candleStick, GraphColors.BrokerCarrierSpot);
    addBottomLine(candleStick, root, GraphColors.BrokerCarrierSpot);
    addTopLine(candleStick, root, GraphColors.BrokerCarrierSpot);
    setWidth(candleStick);

    candleStick.data.setAll(
        brokerCarrierSpotCurrent.map((data) => {
            return {
                category: data?.category,
                average: data['brokerCarrierSpotHistorical']?.average,
                high: data['brokerCarrierSpotHistorical']?.high,
                low: data['brokerCarrierSpotHistorical']?.low,
                date: data['brokerCarrierSpotHistorical']?.date,
            };
        }),
    );

    return candleStick;
}

function createBrokerCarrierSpotCurrentLine(
    root: am5.Root,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    brokerCarrierSpotCurrent: HybridGraphData[],
): am5xy.LineSeries {
    const lineSeries: am5xy.LineSeries = am5xy.LineSeries.new(root, {
        id: bcsCurrentLineSeriesId,
        name: 'HistoricalAverage',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'average',
        categoryXField: 'category',
        stroke: am5.color(GraphColors.BrokerCarrierSpot),
    } as am5xy.ILineSeriesSettings);

    addSquare(lineSeries, root, GraphColors.BrokerCarrierSpot);

    lineSeries.data.setAll(
        brokerCarrierSpotCurrent.map((data) => {
            return {
                category: data?.category,
                average: data['brokerCarrierSpotHistorical']?.average,
                high: data['brokerCarrierSpotHistorical']?.high,
                low: data['brokerCarrierSpotHistorical']?.low,
                date: data['brokerCarrierSpotHistorical']?.date,
            };
        }),
    );

    return lineSeries;
}
//#endregion

//#region SCC Current Series
function createShipperCarrierContractCurrentSeries(
    root: am5.Root,
    chart: am5xy.XYChart,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    shipperCarrierContract: HybridGraphData[],
): (am5xy.LineSeries | am5xy.CandlestickSeries)[] {
    const candleStick: am5xy.CandlestickSeries =
        createShipperCarrierContractCurrentCandleStick(
            root,
            xAxis,
            yAxis,
            shipperCarrierContract,
        );
    const lineSeries: am5xy.LineSeries =
        createShipperCarrierContractCurrentLine(
            root,
            xAxis,
            yAxis,
            shipperCarrierContract,
        );

    chart.series.push(candleStick);
    chart.series.push(lineSeries);

    return [lineSeries, candleStick];
}

function createShipperCarrierContractCurrentCandleStick(
    root: am5.Root,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    shipperCarrierContract: HybridGraphData[],
): am5xy.CandlestickSeries {
    const candleStick: am5xy.CandlestickSeries = am5xy.CandlestickSeries.new(
        root,
        {
            id: sccCurrentCandlestickSeriesId,
            name: 'CurrentHighLow',
            xAxis: xAxis,
            yAxis: yAxis,
            highValueYField: 'high',
            openValueYField: 'high',
            lowValueYField: 'low',
            valueYField: 'low',
            categoryXField: 'category',
            clustered: false,
        } as am5xy.ICandlestickSeriesSettings,
    );

    setCandlestickColor(candleStick, GraphColors.ShipperCarrierContract);
    addBottomLine(candleStick, root, GraphColors.ShipperCarrierContract);
    addTopLine(candleStick, root, GraphColors.ShipperCarrierContract);
    setWidth(candleStick);

    xAxis.data.setAll(Object.values(shipperCarrierContract));
    candleStick.data.setAll(
        shipperCarrierContract.map((data) => {
            return {
                category: data?.category,
                average: data['shipperCarrierContractHistorical']?.average,
                high: data['shipperCarrierContractHistorical']?.high,
                low: data['shipperCarrierContractHistorical']?.low,
                date: data['shipperCarrierContractHistorical']?.date,
            };
        }),
    );

    return candleStick;
}

function createShipperCarrierContractCurrentLine(
    root: am5.Root,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    shipperCarrierContract: HybridGraphData[],
): am5xy.LineSeries {
    const lineSeries: am5xy.LineSeries = am5xy.LineSeries.new(root, {
        id: sccCurrentLineSeriesId,
        name: 'HistoricalAverage',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'average',
        categoryXField: 'category',
        stroke: am5.color(GraphColors.ShipperCarrierContract),
    } as am5xy.ILineSeriesSettings);

    addSquare(lineSeries, root, GraphColors.ShipperCarrierContract);

    lineSeries.data.setAll(
        shipperCarrierContract.map((data) => {
            return {
                category: data?.category,
                average: data['shipperCarrierContractHistorical']?.average,
                high: data['shipperCarrierContractHistorical']?.high,
                low: data['shipperCarrierContractHistorical']?.low,
                date: data['shipperCarrierContractHistorical']?.date,
            };
        }),
    );

    return lineSeries;
}
//#endregion

//#region Shipper Carrier Contract Historical Series
function createShipperCarrierContractHistoricalSeries(
    shipperCarrierContractHistorical,
    {
        chart,
        root,
        xAxis,
        yAxis,
    }: {
        xAxis: am5xy.CategoryAxis<any>;
        yAxis: am5xy.ValueAxis<any>;
        root: am5.Root;
        chart: am5xy.XYChart;
    },
    {
        perLoad,
    }: {
        perLoad: boolean;
    },
): [am5xy.LineSeries, am5xy.CandlestickSeries] {
    const candleStick: am5xy.CandlestickSeries =
        createShipperCarrierContractHistoricalCandleStick(
            root,
            xAxis,
            yAxis,
            shipperCarrierContractHistorical,
        );

    const lineSeries: am5xy.LineSeries =
        createShipperCarrierContractHistoricalLineSeries(
            root,
            xAxis,
            yAxis,
            shipperCarrierContractHistorical,
        );

    chart.series.push(candleStick);
    chart.series.push(lineSeries);

    return [lineSeries, candleStick];
}

function createShipperCarrierContractHistoricalLineSeries(
    root: am5.Root,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    ShipperCarrierContractHistorical: HybridGraphData[],
) {
    const lineSeries: am5xy.LineSeries = am5xy.LineSeries.new(root, {
        id: sccHistoricalLineSeriesId,
        name: 'HistoricalAverage',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'average',
        categoryXField: 'category',
        stroke: am5.color(GraphColors.ShipperCarrierContract),
    } as am5xy.ILineSeriesSettings);

    addSquare(lineSeries, root, GraphColors.ShipperCarrierContract);

    lineSeries.data.setAll(
        ShipperCarrierContractHistorical.map((data) => {
            return {
                category: data?.category,
                average: data['shipperCarrierContractHistorical']?.average,
                high: data['shipperCarrierContractHistorical']?.high,
                low: data['shipperCarrierContractHistorical']?.low,
                date: data['shipperCarrierContractHistorical']?.date,
            };
        }),
    );
    return lineSeries;
}

function createShipperCarrierContractHistoricalCandleStick(
    root: am5.Root,
    xAxis: am5xy.CategoryAxis<any>,
    yAxis: am5xy.ValueAxis<any>,
    ShipperCarrierContractHistorical: HybridGraphData[],
): am5xy.CandlestickSeries {
    const candleStick: am5xy.CandlestickSeries = am5xy.CandlestickSeries.new(
        root,
        {
            id: sccHistoricalCandlestickSeriesId,
            name: 'HistoricalHighLow',
            xAxis: xAxis,
            yAxis: yAxis,
            highValueYField: 'high',
            openValueYField: 'high',
            lowValueYField: 'low',
            valueYField: 'low',
            categoryXField: 'category',
            clustered: false,
        } as am5xy.ICandlestickSeriesSettings,
    );

    setCandlestickColor(candleStick, GraphColors.ShipperCarrierContract);
    addBottomLine(candleStick, root, GraphColors.ShipperCarrierContract);
    addTopLine(candleStick, root, GraphColors.ShipperCarrierContract);
    setWidth(candleStick);
    candleStick.data.setAll(
        ShipperCarrierContractHistorical.map((data) => {
            return {
                category: data?.category,
                average: data['shipperCarrierContractHistorical']?.average,
                high: data['shipperCarrierContractHistorical']?.high,
                low: data['shipperCarrierContractHistorical']?.low,
                date: data['shipperCarrierContractHistorical']?.date,
            };
        }),
    );

    return candleStick;
}
//#endregion

//#region Line Series Utils
function addSquare(series: am5xy.XYSeries, root: am5.Root, color: string) {
    series.bullets.push(() => {
        return am5.Bullet.new(root, {
            sprite: am5.Rectangle.new(root, {
                fill: am5.color(color),
                width: 8,
                height: 8,
                centerX: am5.percent(50),
                centerY: am5.percent(50),
            }),
        });
    });
}
//#endregion

//#region CandleStick Series Utils
function setWidth(series: am5xy.CandlestickSeries) {
    series.columns.template.setAll({
        width: 1,
    });
}

function setCandlestickColor(series: am5xy.CandlestickSeries, color: string) {
    series.columns.template.states.create('riseFromOpen', {
        fill: am5.color(color),
        stroke: am5.color(color),
    });
    series.columns.template.states.create('dropFromOpen', {
        fill: am5.color(color),
        stroke: am5.color(color),
    });
}

function addBottomLine(series: am5xy.XYSeries, root: am5.Root, color: string) {
    series.bullets.push(() => {
        return am5.Bullet.new(root, {
            sprite: createHorizontalLine(root, color),
            locationY: 0,
        });
    });
}

function addTopLine(series: am5xy.XYSeries, root: am5.Root, color: string) {
    series.bullets.push(() => {
        return am5.Bullet.new(root, {
            sprite: createHorizontalLine(root, color),
            locationY: 1,
        });
    });
}

function createHorizontalLine(root: am5.Root, color: string): am5.Rectangle {
    return am5.Rectangle.new(root, {
        fill: am5.color(color),
        width: 8,
        height: 1,
        centerX: am5.percent(50),
        centerY: am5.percent(50),
    });
}

//#endregion

//#region Utils

function getMinMaxYValues(rates: { low: number; high: number }[] = []): {
    min: number;
    max: number;
} {
    const min = sortBy(rates, 'low')?.[0]?.low ?? 0;
    const max = sortBy(rates, 'high')?.reverse()?.[0]?.high ?? 0;

    return { min, max };
}

/**
 * Creates an array of step values from a minimum to a maximum value.
 * This is so we can add the correct label for the Y-Axis.
 *
 * @param min - minimum value
 * @param max - maximum value
 * @param step - step value
 * @returns
 */
function createSteps(min: number, max: number, step: number): number[] {
    const range = [];

    for (let i = min; i <= max; i += step) {
        range.push(i);
    }
    return range;
}

//#endregion
